'use client'

import { Icon } from "@/components/ui/icon"
import {
  Popover,
  cn
} from "@heroui/react"
import { ReactNode } from "react"
import { NotificationsList } from "./NotificationsList"

interface NotificationsPopoverProps {
  isOpen: boolean
  onClose: () => void
  onOpenChange?: (open: boolean) => void
  children: ReactNode
  hasError?: boolean
}

export function NotificationsPopover({
  isOpen,
  onClose,
  onOpenChange,
  children,
  hasError = false
}: NotificationsPopoverProps) {
  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }
        if (!open) onClose();
      }}
      placement="bottom-end"
      classNames={{
        content: "z-50",

        trigger: "overflow-visible"
      }}
      motionProps={{
        variants: {
          enter: {
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.2,
              ease: "easeOut"
            }
          },
          exit: {
            y: -5,
            opacity: 0,
            transition: {
              duration: 0.15,
              ease: "easeIn"
            }
          }
        }
      }}
    >
      <Popover.Trigger>
        {children}
      </Popover.Trigger>
      {/* Popover.Content portals to document.body, which sits inside the
          <html> theme scope, so it resolves the theme tokens without a
          data-theme of its own. */}
      <Popover.Content
        className={cn(
          "w-[min(24rem,calc(100vw-1.5rem))] max-h-[85vh] overflow-hidden p-0 shadow-lg",
          "border border-border rounded-xl flex flex-col bg-overlay text-overlay-foreground"
        )}
      >
        <div className="flex items-center justify-between p-4 border-b w-full border-border shrink-0">
          <div className="cw-ui-heading text-lg text-foreground">Notifications</div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-muted hover:text-foreground hover:bg-surface transition-colors"
            aria-label="Close notifications"
          >
            <Icon icon="x" className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto grow">
          {hasError ? (
            <div className="p-6 text-center">
              <Icon icon="warning" className="w-12 h-12 mx-auto text-warning mb-3" />
              <p className="text-foreground mb-2">Unable to load notifications</p>
              <p className="text-sm text-muted">
                We're having trouble connecting to the notification service.
                Please try again later.
              </p>
            </div>
          ) : (
            <NotificationsList />
          )}
        </div>
      </Popover.Content>
    </Popover>
  )
}
